/**
 * "The server moved this query and we deliberately did NOT refetch it" — an explicit signal, kept
 * OUTSIDE the query cache on purpose (`docs/realtime-updates-plan-v1.md` Phase 6).
 *
 * ## Why not just read the query cache
 *
 * `invalidateQueries` sets `isInvalidated` on the query, and `isStale` derives from it — so either looks
 * like a free answer. Both are wrong here, for two independent reasons:
 *
 *  1. **`isStale` is true routinely.** `main.tsx` sets a global `staleTime: 30_000`, so any query older
 *     than 30 s reads stale whether or not anything changed. A badge on that is lit almost always, which
 *     is the same as no badge.
 *  2. **`isInvalidated` cannot tell whose write it was.** `press-station-page` invalidates its OWN
 *     `['press-worklist']` key in both mutation `onSettled` handlers, so the flag is set by the
 *     operator's own tap as much as by a change on another station. A badge on that lights after every
 *     press — it would be telling the operator about their own action.
 *
 * That second one is the same trap as relying on `structuralSharing` to distinguish server data from an
 * optimistic write: when correctness depends on knowing WHO wrote, the framework flag that does not
 * record the author cannot carry it. So the signal is written by exactly one author — the change-feed
 * watcher, which is the only thing that knows a change came from the server.
 *
 * ## Why a store and not a refetch
 *
 * `press-worklist` is in `MARK_STALE_ONLY` because refetching it under a finger can move a tap onto the
 * wrong garment and fire `settleLine` against it. So the freshness has to be offered, never applied: the
 * operator taps Refresh when their hands are clear. This store is how "there is something to refresh"
 * crosses from the watcher to the button.
 */
import { useSyncExternalStore } from 'react';

const staleKeys = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Called by the change-feed watcher when a `MARK_STALE_ONLY` key's domain moved server-side.
 *
 * The early-out only skips the listener walk. What actually prevents a redundant re-render is
 * `getSnapshot` returning a **boolean**: `useSyncExternalStore` compares snapshots and bails when they
 * are equal. That is load-bearing — return a `Set` or an object from `getSnapshot` and every notification
 * re-renders the press page, which is the one render tree a finger is aimed at.
 */
export function markQueryStale(key: string): void {
  if (staleKeys.has(key)) return;
  staleKeys.add(key);
  emit();
}

/**
 * Called when the query has fresh data again — whether the operator tapped Refresh or a mutation's own
 * refetch landed. Either way the screen is current, so the offer is satisfied.
 */
export function clearQueryStale(key: string): void {
  if (!staleKeys.delete(key)) return;
  emit();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/**
 * Subscribe a component to one key's signal. Returns a boolean, so `getSnapshot` is a primitive and
 * cannot trip `useSyncExternalStore`'s referential-stability check.
 */
export function useQueryStaleSignal(key: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => staleKeys.has(key),
    () => false, // SSR/prerender: never badge on the server
  );
}

/** Tests only. Leaves listeners alone so a mounted component keeps working across a reset. */
export function resetQueryStaleSignals(): void {
  if (staleKeys.size === 0) return;
  staleKeys.clear();
  emit();
}
